import type { AnalyticsEvent, AnalyticsProps } from "./posthog";

/**
 * Meta Pixel — ad attribution for Instagram / Facebook campaigns.
 *
 * The base code lives in the root document (routes/__root.tsx), next to the
 * Google Ads tag and the TikTok pixel, and fires the first PageView. fbevents
 * follows History API navigations on its own, so later route changes send
 * their own PageView without a router subscription.
 *
 * This module only translates our funnel events into Meta standard events. It
 * is called from `track()` in ./posthog.ts, which every funnel event already
 * passes through (booking pages via BookingProvider, stays / bucket / WhatsApp
 * directly), so there is one mapping and no second set of call sites.
 *
 * Activity bookings are pay on arrival, so they are a `Lead`, never a
 * `Purchase`: reporting a payment we never took would mis-train Meta's
 * bidding. Stays really are paid in full online (LiteAPI), so they are a
 * `Purchase` with the charged amount.
 */

type Fbq = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: Fbq;
  }
}

type MetaEvent = {
  name: "ViewContent" | "InitiateCheckout" | "Lead" | "Purchase" | "Contact";
  params: Record<string, unknown>;
  eventId?: string;
};

function str(v: AnalyticsProps[string]): string | undefined {
  return typeof v === "string" && v ? v : undefined;
}

function num(v: AnalyticsProps[string]): number | undefined {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? v : undefined;
}

function contentParams(props: AnalyticsProps): Record<string, unknown> {
  const slug = str(props.activity_slug) ?? str(props.property_slug);
  return {
    ...(slug ? { content_ids: [slug], content_type: "product" } : {}),
    ...(str(props.activity_title) ? { content_name: str(props.activity_title) } : {}),
    ...(str(props.category) ? { content_category: str(props.category) } : {}),
    ...(str(props.city) ? { city: str(props.city) } : {}),
  };
}

/** Our funnel event → the Meta standard event it stands for, or null to skip. */
export function toMetaEvent(event: AnalyticsEvent, props: AnalyticsProps = {}): MetaEvent | null {
  switch (event) {
    case "activity_viewed": {
      const value = num(props.price_from);
      return {
        name: "ViewContent",
        params: { ...contentParams(props), ...(value ? { value, currency: "MAD" } : {}) },
      };
    }
    case "booking_details_started":
      return { name: "InitiateCheckout", params: contentParams(props) };
    case "booking_completed": {
      const eventId = str(props.booking_id);
      if (props.payment === "online") {
        const value = num(props.total);
        const currency = str(props.currency);
        return {
          name: "Purchase",
          params: { ...contentParams(props), ...(value && currency ? { value, currency } : {}) },
          eventId,
        };
      }
      return { name: "Lead", params: contentParams(props), eventId };
    }
    case "whatsapp_clicked":
      return { name: "Contact", params: {} };
    default:
      return null;
  }
}

/** Sends the Meta equivalent of a funnel event. No-op when the pixel is not loaded. */
export function forwardToMetaPixel(event: AnalyticsEvent, props?: AnalyticsProps): void {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  const mapped = toMetaEvent(event, props);
  if (!mapped) return;
  // eventID dedupes refreshes of the thank-you page, and lets a future
  // Conversions API call for the same booking match instead of double-count.
  if (mapped.eventId) window.fbq("track", mapped.name, mapped.params, { eventID: mapped.eventId });
  else window.fbq("track", mapped.name, mapped.params);
}
